import { fetchAuthSession, signOut } from 'aws-amplify/auth';
import { logError, onSessionExpired } from './trackingUtils';

/**
 * Custom error class for API responses with HTTP status codes.
 * Thrown for non-401 errors (401s are handled automatically for authenticated calls).
 */
export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly url: string
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Configuration options for API requests.
 */
export interface CallOptions {
  /** HTTP method (default: 'GET') */
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  /** Request body (will be JSON stringified) */
  body?: object;
  /** Additional headers */
  headers?: Record<string, string>;
  /** Whether this endpoint requires authentication (default: true)
   *  - When true: fetches access token, handles 401 with logout/redirect
   *  - When false: no auth token, no 401 handling
   */
  requiresAuth?: boolean;
}

/**
 * Fetches the current access token from AWS Amplify.
 * Amplify automatically refreshes expired tokens.
 */
async function getAccessToken(): Promise<string> {
  const { tokens } = await fetchAuthSession();
  const token = tokens?.accessToken?.toString();
  if (!token) {
    throw new Error('No access token available');
  }
  return token;
}

/**
 * Handles 401 unauthorized responses by signing out and redirecting to login.
 * This is called automatically - components don't need to handle 401s.
 */
async function handleUnauthorized(url: string): Promise<never> {
  logError(`401 Unauthorized from [${url}]. Signing out and redirecting to login.`,
    new Error('Session expired or invalid token'));

  // Track the session expiration event
  onSessionExpired();

  // Sign out using Amplify (global sign out to revoke tokens server-side)
  try {
    await signOut({ global: true });
  } catch (error) {
    console.warn('Global sign out failed, attempting local sign out:', error);
    try {
      await signOut();
    } catch (e) {
      console.warn('Local sign out also failed:', e);
    }
  }

  // Redirect to login page with expired flag
  // Using window.location for a full page redirect (clears any stale React state)
  window.location.href = '/login?expired=true';

  // This error will never be caught since we're redirecting,
  // but TypeScript needs a return type
  throw new Error('Redirecting to login due to 401');
}

/**
 * Makes an API request with standardized error handling.
 *
 * By default (requiresAuth: true):
 * - Automatically fetches access token from Amplify
 * - Includes Bearer token in Authorization header
 * - Automatically handles 401 responses (signs out, redirects to login)
 *
 * When requiresAuth: false:
 * - No Authorization header
 * - No 401 handling (401 treated like any other error)
 *
 * @param url - The URL to fetch
 * @param options - Optional fetch configuration including `requiresAuth` flag
 * @returns The Response object if successful
 * @throws ApiError for non-ok responses (except 401 when requiresAuth: true, which redirects)
 * @throws Error for network failures or missing token
 */
export async function call(
  url: string,
  options: CallOptions = {}
): Promise<Response> {
  const { method = 'GET', body, headers = {}, requiresAuth = true } = options;

  // Build headers based on authentication mode
  const requestHeaders: Record<string, string> = {
    'Content-Type': 'application/json',
    ...headers,
  };

  if (requiresAuth) {
    const accessToken = await getAccessToken();
    requestHeaders['Authorization'] = `Bearer ${accessToken}`;
  }

  let response: Response;
  try {
    response = await fetch(url, {
      method,
      headers: requestHeaders,
      ...(body && { body: JSON.stringify(body) }),
    });
  } catch (error) {
    logError(`Network error fetching [${url}]:`, error);
    throw new Error(`Network error accessing ${url}`);
  }

  // Handle 401 automatically when auth is required - sign out and redirect
  if (requiresAuth && response.status === 401) {
    await handleUnauthorized(url);
  }

  // Handle other non-ok responses
  if (!response.ok) {
    const apiError = new ApiError(
      `HTTP ${response.status} from ${url}`,
      response.status,
      url
    );
    logError(`API error from [${url}]: ${response.status}`, apiError);
    throw apiError;
  }

  return response;
}
